/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import logo from 'assets/Logo_updated.svg';
import { Menu } from 'components/TopicMenu/Menu';
import { VisuallyHidden } from 'components/UI/VisuallyHidden';
import { Fragment as _, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import getStore from 'services/getStore';
import isIE from 'services/isIE';
import { State } from 'store/store';
import { colors, getButtonFocus, values } from 'styles/variables';
import BackBtn from '../Header/BackBtn';
import ProgressBar from '../Header/ProgressBar';
import { HeaderBackground } from './HeaderBackground';
import { HeaderLayout } from 'components/Header_v2/HeaderLayout';

const store = getStore();

const { lPink, lGrey0 } = colors;
const { headerHeight } = values;

interface IHeader {
  currentTopicIndex: number;
  topics: any[];
  theme: any;
}

const Burger = props => {
  const width = 24;

  // need to get color from theme
  const { color = '#2a2c2d', onClick } = props;

  return (
    <button
      css={css({
        outline: 'none',
        border: 'none',
        background: 'transparent',
        ':focus': {
          boxShadow: getButtonFocus(color).replace('box-shadow: ', ''),
        },
      })}
      aria-label="open menu"
      aria-haspopup="true"
      onClick={onClick}
    >
      <div
        className="fader_-5s"
        css={css({
          width: 24,
          cursor: 'pointer',
        })}
      >
        {[0, 1, 2].map(i => (
          <div
            key={i}
            css={css`
              width: ${width};
              height: 4px;
              background: ${color};
              margin: 2px 0;
            `}
          />
        ))}
      </div>
    </button>
  );
};

const HeaderGraphic = props => {
  const {
    currentTopicIndex = 0,
    topics,
    headerProps,
    isFirstInteraction,
  } = props;

  // console.log({ currentTopicIndex });

  const [menu, setMenuState] = useState(false);
  const currentTopicName = topics[currentTopicIndex]
    ? topics[currentTopicIndex].props.name
    : 'unknown';

  const { theme } = useSelector((state: State) => {
    return state;
  });

  const {
    headerHeight,
    headerBackgroundColor,
    headerBackgroundOpacity,
    includeHeaderOverlay,
    headerOverlayColor,
    headerOverlayOpacity,
    headerTextColor,
    backButton,
    topicName,
    progressBar,
    topicMenu,
    headerDropShadow,
    headerBackground,
    includeLogo = true,
    headerLogo,
  } = headerProps;

  const header = useRef<HTMLElement | null>(null);

  const hideHeader = useSelector((state: any) => {
    return state.hideHeader;
  });

  const hideMenu = useCallback(() => {
    setMenuState(false);
    requestAnimationFrame(() =>
      document.removeEventListener('click', hideMenu)
    );
  }, []);

  useEffect(() => {
    menu && document.addEventListener('click', hideMenu);
  }, [menu]);

  useEffect(() => {
    if (!header.current || window.dev()) {
      return;
    }

    // working on a better solution to all this bouncing around headerHeight stuff
    // feels like using the prop headerHeight for the graphic isn't the right thing
    // might better be named expandedHeight or something that indicates it's temp nature
    // since for the duration of module beyond first interaction the ACTUAL headerHeight becomes
    // 70 > 50 px like the standard
    // could remove headerHeight from both headers props since they're all defaulting to those values ( 70 > 50 )
    // and only graphic header has that expandedHeight

    if (screen.availWidth <= 480) {
      header.current.style.height = '50px';
      return;
    }

    if (currentTopicIndex > 0) {
      header.current.style.height = '70px';
      return;
    }

    // this'll come in a frame later. so the scroll listener below still kicks in
    // needs to be that little bit later to account for the scrollY being 0 when entering the topic
    // but then immediately scrolling to the bottom of the page to satisfy the navigation
    // skinning cats one way
    requestAnimationFrame(() => {
      if (!header.current) {
        return;
      }
      //
      if (window.scrollY > headerHeight) {
        // this check for screen 'should' not be needed since returning above. but...
        header.current.style.height =
          screen.availWidth <= 480 ? '50px' : '70px';
        return;
      }
    });

    //
    //

    const onScroll = () => {
      if (!header.current) {
        return;
      }

      const { scrollY } = window;

      if (scrollY > window.innerHeight) {
        return;
      }

      let invertedScrollY = headerHeight - scrollY;

      if (scrollY > headerHeight - 70) {
        invertedScrollY = 70;
      }

      header.current.style.height = `${invertedScrollY}px`;
    };

    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [currentTopicIndex]);

  if (hideHeader) {
    return null;
  }

  return (
    <HeaderLayout
      headerRef={node => {
        if (node) {
          header.current = node;
        }
      }}
      height={`${!isFirstInteraction ? 70 : headerHeight}px`}
      responsiveHeight={`${
        !isFirstInteraction ? 50 : screen.availWidth >= 480 ? headerHeight : 50
      }px`}
      backButton={backButton}
      currentTopicIndex={currentTopicIndex}
      topics={topics}
      headerTextColor={headerTextColor}
      topicName={topicName}
      currentTopicName={currentTopicName}
      includeLogo={includeLogo}
      headerLogo={headerLogo}
      progressBar={progressBar}
      topicMenu={topicMenu}
      menu={menu}
      setMenuState={setMenuState}
      background={<HeaderBackground />}
      Burger={Burger}
    />
  );
};

export { HeaderGraphic };
